// Composes the `afterToolCall` hook that createChatAgent hands to
// pi-agent-core. Responsibilities, in order:
//
//   1. An optional caller hook (`extraAfterToolCall`) runs first — a
//      focused surface (e.g. KernelChat's loop-breaker) can end a runaway
//      loop by returning `{ terminate: true }`.
//   2. The built-in suggest_kernel terminator, gated per surface. By
//      default a suggest_kernel call ends the run (it's an inert UI hint).
//      ChatConsole opts out (terminateOnSuggestKernel: false): it keeps
//      the loop going so the model can write a closing summary and
//      relocates the Save card to the end at agent_end. Surfaces without
//      that relocation machinery keep the terminate, so a stray call
//      can't leave an un-relocated card inline.
//   3. An oversized-result clamp, applied to EVERY tool (including MCP
//      tools we don't control). Thresholds sit ABOVE the context
//      ceiling — piChat's compaction budget is 700k tokens (~2.8MB) —
//      so a result this clamp touches could never be fully read by the
//      model even in an empty conversation; without the clamp it would
//      enter history whole and then vanish into a silent compaction
//      stub one turn later (a 29MB analytics result once entered
//      history this way and snowballed into a ~400MB persisted chat).
//      The clamp keeps a large head plus a marker telling the model
//      how to reach the rest (from_tool_calls / expand_preview /
//      sub-agents), so nothing a model can genuinely traverse is ever
//      clipped. Oversized `details` (local UI rendering only) are
//      replaced with a small marker object.
//
// Pure and side-effect-free so it can be unit-tested without
// constructing an Agent.

export const MAX_TOOL_CONTENT_BYTES = 3 * 1024 * 1024;
export const TOOL_CONTENT_HEAD_BYTES = 256 * 1024;
export const MAX_TOOL_DETAILS_BYTES = 1024 * 1024;

function contentBytes(content) {
    if (typeof content === "string") return content.length;
    if (!Array.isArray(content)) return 0;
    let n = 0;
    for (const block of content) {
        if (!block) continue;
        if (block.type === "text" && typeof block.text === "string") n += block.text.length;
        else {
            try { n += JSON.stringify(block)?.length || 0; } catch { /* unserializable → 0 */ }
        }
    }
    return n;
}

function flattenText(content) {
    if (typeof content === "string") return content;
    if (!Array.isArray(content)) return "";
    return content
        .map((b) => (b?.type === "text" && typeof b.text === "string" ? b.text : ""))
        .filter(Boolean)
        .join("\n");
}

function truncateContent(content, { toolName, toolCallId, rawBytes, maxContentBytes, headBytes }) {
    const head = flattenText(content).slice(0, headBytes);
    return [{
        type: "text",
        text:
            head +
            `\n\n[TRUNCATED — this ${toolName || "tool"} result was ~${Math.round(rawBytes / 1024)} KB, ` +
            `over the ${Math.round(maxContentBytes / 1024)} KB inline limit (itself larger than the ` +
            `conversation context budget, so the full result could never be read inline); the first ` +
            `${Math.round(headBytes / 1024)} KB is shown above and the rest was NOT retained in context. ` +
            `To work with the full result: if it was a row-producing tool, the rows are still stored under ` +
            `tool_call_id ${toolCallId || "?"} — pipe them into execute_python via from_tool_calls, page ` +
            `windows with expand_preview, or hand the analysis to a sub-agent. Otherwise re-run the call ` +
            `narrower (tighter filters, fewer/smaller fields, server-side aggregation).]`,
    }];
}

// `maxContentBytes` may be a number or a zero-arg function — piChat
// passes a closure derived from the CURRENT model's context window so
// a mid-chat model switch retunes the clamp without rebuilding hooks.
export function makeAfterToolCall({
    extraAfterToolCall = null,
    terminateOnSuggestKernel = true,
    maxContentBytes = MAX_TOOL_CONTENT_BYTES,
    maxDetailsBytes = MAX_TOOL_DETAILS_BYTES,
} = {}) {
    return async (ctx) => {
        let out;
        if (extraAfterToolCall) {
            const r = await extraAfterToolCall(ctx);
            if (r) out = r;
        }
        if (!out && terminateOnSuggestKernel && ctx?.toolCall?.name === "suggest_kernel") {
            out = { terminate: true };
        }

        const result = ctx?.result;
        const rawBytes = contentBytes(result?.content);
        const capBytes = typeof maxContentBytes === "function"
            ? (Number(maxContentBytes()) || MAX_TOOL_CONTENT_BYTES)
            : maxContentBytes;
        if (rawBytes > capBytes) {
            out = {
                ...(out || {}),
                content: truncateContent(result?.content, {
                    toolName: ctx?.toolCall?.name,
                    toolCallId: ctx?.toolCall?.id,
                    rawBytes,
                    maxContentBytes: capBytes,
                    // Head scales down with the cap so a small-context
                    // model's clamp doesn't keep more than the window
                    // could hold anyway.
                    headBytes: Math.min(TOOL_CONTENT_HEAD_BYTES, Math.max(1, Math.floor(capBytes / 4))),
                }),
            };
        }

        let detailsBytes = 0;
        try { detailsBytes = result?.details == null ? 0 : (JSON.stringify(result.details)?.length || 0); }
        catch { detailsBytes = 0; }
        if (detailsBytes > maxDetailsBytes) {
            out = {
                ...(out || {}),
                details: {
                    _truncatedForSize: true,
                    originalBytes: detailsBytes,
                    toolName: ctx?.toolCall?.name,
                },
            };
        }

        return out;
    };
}
